import {UserApi, i18nApi} from '@core/api'
import useJwt from '@/auth/jwt/useJwt'
import {i18n, loadLanguageAsync} from '@/libs/i18n'
import Ability from '@/libs/acl/ability';
import Vue from 'vue';
import {createCounterListener} from "@/libs/counter";

let counterListener;

export default {
    state() {
        let profile = localStorage.getItem('profile');
        let organization = localStorage.getItem('organization');
        let organizations = localStorage.getItem('organizations');
        let runtimeConfig = localStorage.getItem('rc');
        let ability = localStorage.getItem('ability');
        ability = ability ? JSON.parse(ability) : [];

        if (ability.length === 0) {
            ability.push({action: 'read', subject: 'Auth'})
        }

        profile = profile ? JSON.parse(profile) : {
            attributes: {
                subscriber: {id: null, title: null},
                productRequest: {
                    activeReceivedRequestsCount: 0,
                    activeSentRequestsCount: 0,
                },
                system: {env: 'undefined'}
            },
            settings: {}
        };
        let productRequest = profile.attributes ? profile.attributes.productRequest : null;

        return {
            activeReceivedRequestsCount: productRequest ? productRequest.activeReceivedRequestsCount : 0,
            activeSentRequestsCount: productRequest ? productRequest.activeSentRequestsCount : 0,
            profile: profile,
            rootOrganizationState: true,
            organization: organization ? JSON.parse(organization) : {},
            organizations: organizations ? JSON.parse(organizations) : [],
            ability: ability,
            language: i18n.locale,
            isDeclarationApproved: localStorage.getItem('dApproved') === 'yes',
            runtimeConfig: runtimeConfig ? JSON.parse(runtimeConfig) : {},
        };
    },
    getters: {
        env(state) {
            return state.profile.attributes.system.env;
        },
        isDeclarationApproved(state) {
            return state.isDeclarationApproved;
        },
        activeReceivedRequestsCount(state) {
            return state.activeReceivedRequestsCount;
        },
        activeSentRequestsCount(state) {
            return state.activeSentRequestsCount;
        },
        profile(state) {
            return state.profile;
        },
        sendEmailPreferences(state) {
            return state.profile.settings.sendEmailPreferences;
        },
        showSendEmailSettingsStep(state) {
            return state.organization.settings.requestSettings.showSendEmailSettingsStep && state.profile.settings.showSendEmailSettingsStep;
        },
        subscriber(state) {
            return state.profile.attributes.subscriber;
        },
        subscription(state, getters) {
            return getters.profile.attributes.subscription;
        },
        rootOrganizationState(state) {
            return state.rootOrganizationState;
        },
        organization(state) {
            return state.organization;
        },
        organizations(state) {
            return state.organizations;
        },
        language(state) {
            return state.language;
        },
        ability(state) {
            return state.ability;
        },
        attributes(state) {
            return state.profile.attributes;
        },
        runtimeConfig(state) {
            return state.runtimeConfig;
        }
    },

    mutations: {
        declarationApproved(state) {
            state.isDeclarationApproved = true;
            localStorage.setItem('dApproved', 'yes');
        },
        clearDeclarationApprovedState(state) {
            state.isDeclarationApproved = false;
            localStorage.removeItem('dApproved');
        },
        setProfile(state, profile) {
            state.profile = Object.assign({}, state.profile, profile);
            if (profile.attributes) {
                if (profile.attributes.productRequest) {
                    state.activeReceivedRequestsCount = profile.attributes.productRequest.activeReceivedRequestsCount;
                    state.activeSentRequestsCount = profile.attributes.productRequest.activeSentRequestsCount;
                }
            }
            localStorage.setItem('profile', JSON.stringify(profile));
        },
        setOrganization(state, organization) {
            state.organization = organization;
            localStorage.setItem('organization', JSON.stringify(organization));
        },
        setOrganizations(state, organizations) {
            state.organizations = organizations;
            localStorage.setItem('organizations', JSON.stringify(organizations));
        },
        setAbility(state, ability) {
            state.ability = ability;

            if (null === ability || ability.length === 0) {
                localStorage.removeItem('ability');
            } else {
                Ability.update(ability);
                localStorage.setItem('ability', JSON.stringify(ability));
            }
        },
        setLanguage(state, language) {
            state.language = language;
        },
        updateSendEmailPreferences(state, preferences) {
            state.profile.settings.sendEmailPreferences = {
                ...preferences,
                transportConfiguration: preferences.configuration
            };
            localStorage.setItem('profile', JSON.stringify(state.profile));
        },
        doNotShowSendEmailSettingsStep(state) {
            state.profile.settings.showSendEmailSettingsStep = false;
            localStorage.setItem('profile', JSON.stringify(state.profile));
        },
        clearRuntimeConfig(state) {
            state.runtimeConfig = {};
            localStorage.setItem('rc', '{}');
            localStorage.setItem('rc_ar', '[]');
        },
        setRuntimeProperty(state, property) {
            state.runtimeConfig[property.name] = property.value;
            localStorage.setItem('rc', JSON.stringify(state.runtimeConfig));

            if (property.removeOnOrganizationSwitch) {
                const autoRemoveConfig = JSON.parse(localStorage.getItem('rc_ar') || '[]');
                if (!autoRemoveConfig.includes(property.name)) {
                    autoRemoveConfig.push(property.name);
                    localStorage.setItem('rc_ar', JSON.stringify(autoRemoveConfig));
                }
            }
        },
        updateShowSendEmailSettingsStepForOrganization(state, value) {
            state.organization.settings.requestSettings.showSendEmailSettingsStep = value;
            localStorage.setItem('organization', JSON.stringify(state.organization));
        },
        updateRootOrganizationState(state, value) {
            state.rootOrganizationState = value;
        },
        autoRemoveRuntimeProperties(state) {
            //  auto remove runtime properties marked with removeOnOrganizationSwitch
            const autoRemoveConfig = JSON.parse(localStorage.getItem('rc_ar') || '[]');
            autoRemoveConfig.forEach((name) => {
                if (state.runtimeConfig.hasOwnProperty(name)) {
                    delete state.runtimeConfig[name];
                }
            });
            localStorage.setItem('rc', JSON.stringify(state.runtimeConfig));
            localStorage.setItem('rc_ar', '[]');
        },
        setCounters(state, counters) {
            Object.keys(counters).forEach(key => {
                state[key] = counters[key];
            });
        }
    },
    actions: {
        setLanguage({commit}, language) {
            loadLanguageAsync(language);
            i18nApi
                .switchLanguage(language)
                .then(() => {
                    commit('setLanguage', language);
                })
            ;
        },

        declarationApproved({commit}) {
            commit('declarationApproved');
        },

        swiftLoginSuccessful({commit}) {
            commit('clearDeclarationApprovedState');
        },

        accessTokenRefreshed({commit}) {
            commit('clearDeclarationApprovedState');
        },

        updateUserProfile({commit, dispatch, state}) {
            return UserApi
                .getProfile()
                .then(response => {
                    commit('setProfile', response);
                    commit('setAbility', response.abilities);
                    commit('autoRemoveRuntimeProperties');

                    if (response.attributes.organization) {
                        commit('setOrganization', response.attributes.organization);
                    }

                    if (response.settings.language !== state.language) {
                        commit('setLanguage', response.settings.language);
                        loadLanguageAsync(response.settings.language);
                    }

                    dispatch('loggedIn')
                });
        },

        selectOrganization({commit, dispatch, state}, organization) {
            return new Promise((resolve, reject) => {
                UserApi
                    .selectOrganization(organization.id)
                    .then(() => {
                        if (!state.organizations.find(o => o.id === organization.id)) {
                            state.organizations.push(organization);
                            commit('setOrganizations', state.organizations);
                        }

                        commit('setOrganization', organization)

                        return dispatch('updateUserProfile')
                            .then(resolve)
                            .catch(reject)
                        ;
                    })
                    .catch(reject)
                ;
            });
        },

        updateShowSendEmailSettingsStepForOrganization({commit}, value) {
            commit('updateShowSendEmailSettingsStepForOrganization', value);
        },

        rootOrganizationUpdated({commit, state}) {
            commit('updateRootOrganizationState', !state.rootOrganizationState);
        },

        updateSendEmailPreferences({commit}, preferences) {
            return new Promise((resolve, reject) => {
                UserApi
                    .updateEmailPreferences(preferences)
                    .then(() => {
                        commit('updateSendEmailPreferences', preferences);
                        resolve();
                    })
                    .catch(reject)
                ;
            });
        },

        doNotShowSendEmailSettingsStep({commit}) {
            return new Promise((resolve, reject) => {
                UserApi
                    .updateSettings({
                        showSendEmailSettingsStep: false
                    })
                    .then(() => {
                        commit('doNotShowSendEmailSettingsStep');
                        resolve();
                    })
                    .catch(reject)
                ;
            });
        },

        setRuntimeProperty({commit}, property) {
            commit('setRuntimeProperty', property);
        },

        refreshCounters() {
            if (counterListener) {
                counterListener.refresh();
            }
        },

        initialized({commit}) {
            counterListener = createCounterListener((data) => {
                commit('setCounters', data);
            });
            counterListener.listen();
        },

        loggedIn() {
            counterListener.listen();
        },

        logout({commit, dispatch}) {
            localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
            localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

            counterListener.stop();

            commit('setProfile', {})
            commit('setOrganization', {})
            commit('setOrganizations', [])
            commit('setAbility', null)
            commit('clearDeclarationApprovedState')
            commit('clearRuntimeConfig')
        }
    }
};
